import React, { FunctionComponent, useState, useEffect, useCallback } from "react";
import { Task, Claim } from "../../../../types";
import "../DetailView.css";
import Modal from "../../../components/Modal";
import SegmentedControl from "../../../components/segmented-control/SegmentedControl";
import { getStatusBadgeColor } from "../../../utils";
import ErrorAlert from "../../../components/error-alert/ErrorAlert";
import RetryButton from "../../../components/error-alert/RetryButton";

const ACTIVE_TASKS = "active";
const ALL_TASKS = "all";

type TasksProps = {
  customerId: number;
  dataSelected: Claim;
  tasks: Task[];
  requestGetTasksByClaimId: (id: number, customerId: number) => Promise<any>;
};

const Tasks: FunctionComponent<TasksProps> = (props) => {
  const { dataSelected, tasks, requestGetTasksByClaimId, customerId } = props;
  const [showNoteModal, setShowNotModal] = useState(false);
  const [note, setNote] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [selectedSegment, setSelectedSegment] = useState(ACTIVE_TASKS);
  const [showError, setShowError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const loadTasks = useCallback(() => {
    setIsFetching(true);
    setShowError(false);
    requestGetTasksByClaimId(dataSelected.id, customerId)
      .catch(() => setShowError(true))
      .finally(() => setIsFetching(false));
  }, [requestGetTasksByClaimId, dataSelected, customerId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const getTask = (task: Task) => (
    <tr key={task.id}>
      <td className="td-style" style={{ padding: "0.35rem" }}>
        <div>{task.assignedTo}</div>
      </td>
      <td
        className="td-style"
        style={{
          width: "20px",
          padding: "0.35rem",
          textAlign: "center",
        }}>
        {task.status === "Active" && task.overdue ? (
          <i
            className="fas fa-exclamation-triangle"
            style={{
              color: "rgb(220,53,69)",
              fontSize: "0.9rem",
            }}></i>
        ) : null}
      </td>
      <td className="td-style" style={{ padding: "0.35rem" }}>
        {task.status === "Active" && task.overdue ? (
          <div style={{ color: "rgb(220,53,69)" }}>
            {new Date(task.dueBy).toLocaleDateString("en-NZ")}
          </div>
        ) : (
          <div>{new Date(task.dueBy).toLocaleDateString("en-NZ")}</div>
        )}
      </td>
      {selectedSegment === ALL_TASKS && (
        <td className="td-style" style={{ padding: "0.35rem" }}>
          <span
            className="badge badge-pill"
            style={{
              padding: "0.25rem 0.5rem",
              fontWeight: 500,
              marginRight: "0.5rem",
              marginBottom: "0.25rem",
              fontSize: "11px",
              color: getStatusBadgeColor(task.status).text,
              backgroundColor: getStatusBadgeColor(task.status).background,
            }}>
            {task.status}
          </span>
        </td>
      )}
      <td className="td-style" style={{ padding: "0.35rem" }}>
        <div style={task.insuredContact ? { fontWeight: "bold" } : {}}>{task.topicDescription}</div>
      </td>
      <td className="td-style" style={{ padding: "0.35rem" }}>
        {task.insuredContact ? (
          <i
            title="Customer has been contacted"
            className="fas fa-flag"
            style={{ color: "rgba(216,183,8)" }}></i>
        ) : (
          <></>
        )}
      </td>
      <td className="td-style" style={{ padding: "0.35rem" }}>
        {task.note ? (
          <div style={{ cursor: "pointer" }}>
            <button
              type="button"
              className="btn btn-sm btn-secondary text-nowrap"
              style={{ fontSize: "0.8rem", lineHeight: 1 }}
              onClick={() => showNote(task.note, getNoteTitle(task))}>
              Show Note
            </button>
          </div>
        ) : null}
      </td>
    </tr>
  );

  const getNoteTitle = (task: Task) => {
    return `"${task.topicDescription}", assigned to ${task.assignedTo} (due by ${task.dueBy})`;
  };

  const getTasks = () => {
    if (tasks.length === 0 || !tasks) {
      return <div>Currently no tasks.</div>;
    }

    return (
      <table className="table table-striped">
        <thead>
          <tr>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Assigned To
            </th>
            <th className="td-style" />
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Due By
            </th>
            {selectedSegment === ALL_TASKS && (
              <th className="td-style" style={{ padding: "0.35rem" }}>
                Status
              </th>
            )}
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Topic Description
            </th>
            <th className="td-style" style={{ padding: "0.35rem" }}></th>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Notes
            </th>
          </tr>
        </thead>
        <tbody>
          {selectedSegment === ALL_TASKS
            ? tasks.map((task: Task) => getTask(task))
            : tasks
                .filter((task: Task) => task.status === "Active")
                .map((task: Task) => getTask(task))}
        </tbody>
      </table>
    );
  };

  const showNote = (note: any, title: string) => {
    let res = note.replace(/<style\b[^>]*>.*?<\/style>/.exec(note), "");
    res = res.replace(/FONT-SIZE: 13px/gi, "");
    res = res.replace(/FONT-SIZE: 10pt/gi, "");
    res = res.replace(/FONT-FAMILY: Arial, sans-serif/gi, "");

    setShowNotModal(true);
    setNote(res);
    setNoteTitle(title);
  };

  const handleTaskSwitch = (segment: string) => {
    if (selectedSegment === segment) {
      return;
    }

    setSelectedSegment(segment);
  };

  return (
    <>
      {showNoteModal && (
        <Modal
          title={noteTitle}
          secondaryActionLabel="Close"
          secondaryAction={() => setShowNotModal(false)}
          width="xl">
          <div dangerouslySetInnerHTML={{ __html: note }} />
        </Modal>
      )}

      <div className="detail-wrapper">
        <div className="task-wrapper">
          <div className="heading">Tasks</div>
          {tasks && tasks.length > 0 && (
            <div>
              <SegmentedControl
                onChange={handleTaskSwitch}
                keyOptionOne={ACTIVE_TASKS}
                labelOptionOne="Active Tasks"
                keyOptionTwo={ALL_TASKS}
                labelOptionTwo="All Tasks"
              />
            </div>
          )}
        </div>
        {isFetching && (
          <div
            className="d-flex justify-content-center"
            style={{
              paddingTop: "1rem",
            }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}
        {showError && !isFetching && (
          <div
            style={{
              display: "flex",
              flex: "0 0 auto",
              paddingTop: "1rem",
              justifyContent: "center",
            }}>
            <ErrorAlert lightMode>
              <span style={{ paddingRight: "1rem" }}>
                Error while loading the tasks of the selected claim. Please retry or submit a
                Support Desk ticket.
              </span>
              <RetryButton retryFunction={loadTasks} />
            </ErrorAlert>
          </div>
        )}
        {!showError && !isFetching && <div className="detail-content">{getTasks()}</div>}
      </div>
    </>
  );
};

export default Tasks;
